import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, Download } from "lucide-react";

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("skip");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showFallback, setShowFallback] = useState(false);

  const downloadTemplate = async (format) => {
    setShowFallback(true);
    try {
      const res = await api.get(`/units-template?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stok_sablonu.${format === "xlsx" ? "xlsx" : "csv"}`;
      a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("İndirme başladı. Dosya inmezse aşağıdaki doğrudan indirme bağlantısını kullanın.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handlePreview = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post("/units/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post(`/units/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`İçe aktarıldı: ${data.created} yeni, ${data.updated} güncellendi, ${data.skipped} atlandı`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">Excel / CSV Stok Yükleme</h1>
        <p className="text-slate-500 mt-1">Bağımsız bölümleri toplu olarak içe aktarın.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadTemplate("xlsx")}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"
                  data-testid="download-xlsx-btn">
            <Download className="w-4 h-4" /> Excel Şablonu İndir
          </button>
          <button onClick={() => downloadTemplate("csv")}
                  className="bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"
                  data-testid="download-csv-btn">
            <Download className="w-4 h-4" /> CSV Şablonu İndir
          </button>
        </div>

        {showFallback && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="template-fallback">
            <div className="font-semibold text-amber-900 mb-1">Otomatik indirme başlamadıysa…</div>
            <div className="text-amber-800 text-xs mb-2">Bazı tarayıcı/önizleme ortamlarında otomatik indirme engellenebilir. Aşağıdaki linklere tıklayarak şablonu doğrudan açın/indirin:</div>
            <div className="flex flex-wrap gap-2">
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/units-template?format=xlsx`}
                 target="_blank" rel="noreferrer"
                 download="stok_sablonu.xlsx"
                 data-testid="template-fallback-xlsx"
                 className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-amber-900 hover:bg-amber-100 inline-flex items-center gap-2">
                <Download className="w-4 h-4" /> Excel Şablonunu Doğrudan Aç / İndir
              </a>
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/units-template?format=csv`}
                 target="_blank" rel="noreferrer"
                 download="stok_sablonu.csv"
                 data-testid="template-fallback-csv"
                 className="px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-amber-900 hover:bg-amber-100 inline-flex items-center gap-2">
                <Download className="w-4 h-4" /> CSV Şablonunu Doğrudan Aç / İndir
              </a>
              <button type="button" onClick={() => setShowFallback(false)}
                      className="text-xs text-amber-700 hover:text-amber-900 underline">Gizle</button>
            </div>
            <div className="text-[11px] text-amber-700 mt-2">Not: Doğrudan link tarayıcıda oturum gerektirir. Otomatik indirme zaten başladıysa bu bölümü görmezden gelebilirsiniz.</div>
          </div>
        )}

        <div className="text-sm text-slate-700">
          <p className="mb-2">Beklenen kolonlar:</p>
          <code className="text-xs bg-slate-100 p-2 rounded block">
            Proje adı, Blok adı, Kat adı, Kat sırası, Kat tipi, Bölüm no, Bölüm tipi, Oda tipi, Plan tipi, Net m², Brüt m², Cephe, Manzara, Kat konumu, Fiyat, Para birimi, Durum, Sorumlu satışçı e-posta, Kat planı URL, Daire planı URL, Örnek görsel URL'leri, Video URL, Kat konum planı URL, Mimari çizim URL, Not
          </code>
          <p className="mt-2 text-xs text-slate-500">Birden fazla örnek görsel URL'sini | ile ayırın.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])}
                 className="w-full min-w-0 max-w-full text-sm sm:w-auto" data-testid="import-file-input" />
          <button onClick={handlePreview} disabled={!file || loading}
                  className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg disabled:opacity-50 inline-flex items-center gap-2" data-testid="preview-btn">
            <Upload className="w-4 h-4" /> Önizleme
          </button>
          {preview && (
            <>
              <select className="input w-auto" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="import-mode">
                <option value="skip">Eşleşeni atla</option>
                <option value="update">Eşleşeni güncelle</option>
              </select>
              <button onClick={handleImport} disabled={loading}
                      className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:opacity-50"
                      data-testid="confirm-import-btn">
                İçe Aktar
              </button>
            </>
          )}
        </div>

        {preview && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-medium">Toplam: {preview.total} satır</span>
              {preview.error_count > 0 ? (
                <span className="text-sm text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {preview.error_count} hatalı satır</span>
              ) : (
                <span className="text-sm text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Hata yok</span>
              )}
            </div>
            <div className="max-h-80 overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Proje</th><th className="p-2 text-left">Blok</th><th className="p-2 text-left">Kat</th><th className="p-2 text-left">Bölüm</th><th className="p-2 text-left">Fiyat</th><th className="p-2 text-left">Durum</th><th className="p-2 text-left">Hata</th></tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 50).map((r) => (
                    <tr key={r.row} className={r.errors.length ? "bg-red-50" : "border-t border-slate-100"}>
                      <td className="p-2 text-slate-500">{r.row}</td>
                      <td className="p-2">{r.data.project_name}</td>
                      <td className="p-2">{r.data.block_name}</td>
                      <td className="p-2">{r.data.floor_name}</td>
                      <td className="p-2">{r.data.unit_no}</td>
                      <td className="p-2">{r.data.price}</td>
                      <td className="p-2">{r.data.status}</td>
                      <td className="p-2 text-red-600">{r.errors.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="border-t pt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
              <div className="font-semibold text-emerald-800">İçe aktarma tamamlandı</div>
              <div className="text-emerald-700 mt-1">
                Yeni: <b>{result.created}</b> • Güncellenen: <b>{result.updated}</b> • Atlanan: <b>{result.skipped}</b>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
